import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileUploadField } from './FileUploadField';
import { VersionNameField } from './VersionNameField';
import { ConfirmModal } from '@/components/ConfirmModal';
import { ErrorBanner } from '@/components/ErrorBanner';
import { ReadonlyVersionInfoView } from './ReadonlyVersionInfoView';
import { parseVersionString } from './versionName';
import { useFirmwareMutations, useFirmwareFile, useFirmwareVersionEditor } from '../../hooks';
import type { Firmware } from '../../types';
import type { EditableDropdownItem } from '@/components/EditableDropdown';
import { FormField } from '@/components/FormField';

interface FirmwareDraftViewProps {
  modelId: number;
  firmware: Firmware;
  firmwareTypeItems: EditableDropdownItem[];
  onReleased: () => void;
  onDeleted: () => void;
}

export function FirmwareDraftView({
  modelId,
  firmware,
  firmwareTypeItems,
  onReleased,
  onDeleted,
}: FirmwareDraftViewProps) {
  const { t } = useTranslation();

  // The number part stays locked; only the date/time part is editable in the draft.
  const initialVersion = useMemo(() => parseVersionString(firmware.versionString), [firmware.versionString]);
  const editor = useFirmwareVersionEditor({
    modelId,
    initialFirmwareTypeId: firmware.firmwareTypeID,
    initialCountryId: firmware.countryID,
    initialNumberPart: initialVersion.numberPart,
    initialDateTimePart: initialVersion.dateTimePart,
    initialFirmwareName: firmware.firmwareName ?? '',
    initialSavedVersion: firmware.versionString.trim(),
  });

  // Re-uploading re-derives the number so the version stays aligned with the new file.
  const fileUpload = useFirmwareFile({
    modelId,
    initiallyUploaded: firmware.fileUploaded,
    onFileDerived: editor.applyUploadedFile,
  });

  const { updateVersion, release, remove } = useFirmwareMutations();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);

  const isReleasing = release.isPending;
  const isDeleting = remove.isPending;

  const resolvedType = useMemo(
    () => firmwareTypeItems.find((item) => item.id === firmware.firmwareTypeID)?.label || firmware.firmwareTypeID.toString(),
    [firmwareTypeItems, firmware.firmwareTypeID]
  );
  const resolvedCountry = useMemo(
    () => firmware.countryID === null ? t('firmware.filters.global') : firmware.countryID,
    [firmware.countryID, t]
  );

  // Saves the version/name changes and then, if a new file was picked, uploads it — a single
  // action replacing the former separate save and upload buttons. Baselines advance on success
  // so the button hides immediately; a failed upload keeps the file so it can be retried.
  const hasPendingChanges = editor.hasUnsavedChanges || fileUpload.file !== null;
  const showSaveButton = hasPendingChanges &&
    editor.isVersionComplete &&
    editor.versionStatus !== 'exists';
  const canSave = !updateVersion.isPending &&
    !fileUpload.isUploading &&
    editor.versionStatus !== 'checking' &&
    (!editor.versionChanged || editor.versionStatus === 'available');

  const handleSave = useCallback(async () => {
    try {
      if (editor.hasUnsavedChanges) {
        await updateVersion.mutateAsync({
          modelId,
          firmwareId: firmware.id,
          versionName: editor.versionName.trim(),
          firmwareName: editor.firmwareName.trim() || undefined,
        });
      }
      if (fileUpload.file) {
        await fileUpload.uploadTo(firmware.id);
      }
      editor.markSaved();
      toast.success(t('firmware.sidePanel.versionUpdateSuccess'));
    } catch {
      toast.error(t('firmware.sidePanel.versionUpdateError'));
    }
  }, [editor, fileUpload, modelId, firmware.id, updateVersion, t]);

  const handleRelease = useCallback(() => {
    release.mutate(
      { modelId, firmwareId: firmware.id },
      {
        onSuccess: () => {
          setShowReleaseConfirm(false);
          onReleased();
        },
        onError: (error) => {
          console.error('Release failed:', error);
          setShowReleaseConfirm(false);
        }
      }
    );
  }, [modelId, firmware.id, release, onReleased]);

  const canRelease = fileUpload.hasUploadedFile &&
    !fileUpload.file &&
    !fileUpload.isUploading &&
    !isReleasing &&
    !fileUpload.showFileUpload;

  const handleDelete = useCallback(() => {
    setDeleteError(null);
    remove.mutate(
      { modelId, firmwareIds: [firmware.id] },
      {
        onSuccess: () => {
          setShowDeleteConfirm(false);
          onDeleted();
        },
        onError: (error) => {
          console.error('Delete failed:', error);
          setShowDeleteConfirm(false);
          setDeleteError(t('firmware.sidePanel.deleteError'));
        },
      }
    );
  }, [modelId, firmware.id, remove, onDeleted, t]);

  return (
    <div className="flex w-full gap-4 flex-col">

      <VersionNameField
        numberPart={editor.numberPart}
        dateTimePart={editor.dateTimePart}
        onDateTimeChange={editor.setDateTimePart}
        status={editor.versionStatus}
      />


      <FormField
        labelKey="firmware.sidePanel.firmwareName"
        label={t('firmware.sidePanel.firmwareName')}
        name="firmwareName"
        type="text"
        value={editor.firmwareName}
        onChange={(e) => editor.setFirmwareName(e.target.value)}
        className="sm:col-span-2"
      />



      <ReadonlyVersionInfoView
        versionString={firmware.versionString}
        resolvedType={resolvedType}
        resolvedCountry={resolvedCountry}
        fileUploaded={firmware.fileUploaded}
        showFile={false}
        showVersion={false}
      />

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-onsurface">
            {t('firmware.sidePanel.file')}
          </label>
          {fileUpload.hasUploadedFile && (
            <button
              type="button"
              onClick={() => fileUpload.setShowFileUpload(!fileUpload.showFileUpload)}
              className="text-sm text-primary hover:text-primary-darker underline"
            >
              {fileUpload.showFileUpload ? t('common.cancel') : t('firmware.sidePanel.uploadNewFile')}
            </button>
          )}
        </div>
        {fileUpload.hasUploadedFile && !fileUpload.file && !fileUpload.showFileUpload && (
          <p className="text-sm text-green-600 mb-2">{t('firmware.sidePanel.fileUploaded')}</p>
        )}
        {!fileUpload.hasUploadedFile && !fileUpload.file && (
          <p className="text-sm text-muted-foreground mb-2">{t('firmware.sidePanel.noFile')}</p>
        )}
        {(fileUpload.showFileUpload || !fileUpload.hasUploadedFile || fileUpload.file) && (
          <FileUploadField file={fileUpload.file} onFileSelect={fileUpload.selectFile} />
        )}
      </div>

      <ErrorBanner hasError={!!deleteError}>
        {deleteError}
      </ErrorBanner>

      {showSaveButton && (
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="w-full py-2 px-4 text-sm font-medium rounded-lg border border-border bg-surface text-onsurface hover:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none"
        >
          {updateVersion.isPending || fileUpload.isUploading ? t('firmware.sidePanel.saving') : t('firmware.sidePanel.saveVersionButton')}
        </button>
      )}

      <div className="pt-4 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => setShowReleaseConfirm(true)}
          disabled={!canRelease}
          className="flex-1 py-2 px-4 text-sm font-medium rounded-lg bg-primary text-onprimary shadow-2xs hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:ring-2 focus:ring-primary"
        >
          {isReleasing ? t('firmware.sidePanel.saving') : t('firmware.sidePanel.releaseButton')}
        </button>
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isDeleting || isReleasing || fileUpload.isUploading}
          className="flex-1 py-2 px-4 text-sm font-medium rounded-lg border border-error text-error hover:bg-error/10 disabled:opacity-50 disabled:pointer-events-none"
        >
          {isDeleting ? t('firmware.sidePanel.deleting') : t('firmware.sidePanel.deleteButton')}
        </button>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title={t('firmware.sidePanel.deleteConfirmTitle')}
        message={t('firmware.sidePanel.deleteConfirmMessage')}
        confirmLabel={t('firmware.sidePanel.deleteButton')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isLoading={isDeleting}
        variant="danger"
      />

      <ConfirmModal
        isOpen={showReleaseConfirm}
        title={t('firmware.sidePanel.releaseConfirmTitle')}
        message={t('firmware.sidePanel.releaseConfirmMessage')}
        confirmLabel={t('firmware.sidePanel.releaseButton')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleRelease}
        onCancel={() => setShowReleaseConfirm(false)}
        isLoading={isReleasing}
      />
    </div>
  );
}
